import classNames from 'classnames'
import clamp from 'lodash/clamp'
import type { FunctionComponent, HTMLAttributes } from 'react'
import { Children } from 'react'

import styles from './row.module.scss'

/** A simple responsive components that renders a row of elements that autowraps. */
export const Row: FunctionComponent<React.PropsWithChildren<Props>> = ({
    children,
    tag: Tag = 'div',
    className,
    fillRow = true,
    maxColumns = 3,
    spacing = 'default',
    align = 'left',
    ...restOfProps
}) => {
    const childrenCount = Children.toArray(children)?.length ?? 0

    const columnCount = calculateColumns(
        childrenCount,
        Math.min(
            maxColumns,
            (!fillRow && childrenCount > 4) || childrenCount % 4 === 0 ? 4 : 3,
        ),
    )

    return (
        <Tag
            className={classNames(
                styles.row,
                styles[`spacing-${spacing}`],
                styles[`row-col-${columnCount}`],
                styles[`align-${align}`],
                className,
            )}
            {...restOfProps}
        >
            {children}
        </Tag>
    )
}

interface Props extends Omit<HTMLAttributes<HTMLElement>, 'className' | 'ref'> {
    /** Appends this className to the rest of classNames. Does not overwrite. */
    className?: string
    /** The <tag to render in DOM. */
    tag?: 'div' | 'p' | 'ul' | 'ol'
    /** If true, rows will always layout in such a way that there is no empty columns **/
    fillRow?: boolean
    /** Maximum columns. **/
    maxColumns?: number
    /** Spacing between columns. */
    spacing?: 'default' | 'compact'
    /** Horizontal alignment for items in partial rows. */
    align?: 'left' | 'center' | 'right'
}

function calculateColumns(itemCount: number, columnCount: number): number {
    if (columnCount === 1) return 1
    const rowCount = Math.floor(itemCount / columnCount)
    return Math.floor(clamp(itemCount / rowCount, 1, columnCount))
}
